/**
 * Helpers for the RHF form that edits an arbitrary extractedFields object.
 *
 * The document schema is fully dynamic (each document type has its own
 * fields), so we build defaults and a coerced-output mapper at runtime
 * rather than a compile-time zod schema.
 */

export type FieldValues = Record<string, unknown>;

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

/**
 * True when `value` is an array whose items are all plain objects with the
 * same keys — i.e. something we can render as an editable table.
 */
export function isArrayOfObjects(
  value: unknown,
): value is Record<string, unknown>[] {
  if (!Array.isArray(value) || value.length === 0) return false;
  const first = value[0];
  if (!isPlainObject(first)) return false;
  const keys = Object.keys(first).sort().join("|");
  return value.every(
    (v) => isPlainObject(v) && Object.keys(v).sort().join("|") === keys,
  );
}

/**
 * True when `value` is a non-empty array of scalar primitives — what the
 * editable list editor renders one input per item for (e.g. nor_date_list).
 */
export function isArrayOfPrimitives(
  value: unknown,
): value is Array<string | number | boolean | null> {
  if (!Array.isArray(value) || value.length === 0) return false;
  return value.every(
    (v) =>
      v === null ||
      typeof v === "string" ||
      typeof v === "number" ||
      typeof v === "boolean",
  );
}

/** Seed RHF defaults; array-of-objects stays structured, other complex
 *  shapes stringify to JSON for a textarea fallback.
 */
export function toFormDefaults(source: Record<string, unknown>): FieldValues {
  const out: FieldValues = {};
  for (const [key, value] of Object.entries(source)) {
    if (
      value === null ||
      typeof value === "string" ||
      typeof value === "number" ||
      typeof value === "boolean"
    ) {
      out[key] = value;
    } else if (isArrayOfObjects(value)) {
      out[key] = value.map((row) => ({ ...row }));
    } else if (isArrayOfPrimitives(value)) {
      out[key] = [...value];
    } else {
      out[key] = JSON.stringify(value, null, 2);
    }
  }
  return out;
}

export interface ParseResult {
  values: Record<string, unknown>;
  errors: Record<string, string>;
}

/**
 * Convert the form state back to the shape the API expects. Complex
 * fields are parsed from JSON; per-field parse errors are returned so
 * the caller can prompt the user to fix them.
 */
export function fromFormValues(
  source: Record<string, unknown>,
  formValues: FieldValues,
): ParseResult {
  const values: Record<string, unknown> = {};
  const errors: Record<string, string> = {};
  for (const key of Object.keys(source)) {
    const original = source[key];
    const input = formValues[key];
    if (typeof original === "number") {
      if (input === "" || input === null || input === undefined) {
        values[key] = null;
        continue;
      }
      const num = Number(input);
      if (Number.isNaN(num)) {
        errors[key] = "Must be a number";
      } else {
        values[key] = num;
      }
    } else if (typeof original === "boolean") {
      values[key] = Boolean(input);
    } else if (
      original === null ||
      typeof original === "string"
    ) {
      values[key] = input ?? null;
    } else if (isArrayOfObjects(original)) {
      // Table-edited structured array; pass through as-is.
      values[key] = Array.isArray(input) ? input : [];
    } else if (isArrayOfPrimitives(original)) {
      // Editable list of scalars — coerce each item to the original
      // item type so numbers round-trip as numbers rather than digit
      // strings. Empty / null inputs become nulls.
      const sampleItem = original.find((v) => v !== null);
      const itemType = typeof sampleItem;
      const arr = Array.isArray(input) ? input : [];
      values[key] = arr.map((item) => {
        if (item === null || item === undefined || item === "") return null;
        if (itemType === "number") {
          const raw = typeof item === "string" ? item.replace(/,/g, "") : item;
          const n = Number(raw);
          return Number.isNaN(n) ? null : n;
        }
        if (itemType === "boolean") {
          return Boolean(item);
        }
        return String(item);
      });
    } else {
      // Other complex shapes: parse the textarea JSON fallback.
      try {
        values[key] =
          typeof input === "string" ? JSON.parse(input) : input;
      } catch (err) {
        errors[key] =
          err instanceof Error ? err.message : "Invalid JSON";
      }
    }
  }
  return { values, errors };
}
